/**
 * Typed HTTP client that replaces the Electron window.api bridge.
 * All API calls go through this module with Firebase auth token attached.
 */

let getToken: (() => Promise<string | null>) | null = null;

export function setTokenProvider(provider: () => Promise<string | null>) {
  getToken = provider;
}

const BASE_URL = import.meta.env.VITE_API_URL ?? '';

/** Build a full URL from BASE_URL + path, with optional query params. */
function buildUrl(path: string, query?: Record<string, string>): string {
  const url = new URL(`${BASE_URL}${path}`, window.location.origin);
  if (query) {
    for (const [k, v] of Object.entries(query)) {
      if (v != null) url.searchParams.set(k, v);
    }
  }
  return url.toString();
}

async function authHeaders(): Promise<HeadersInit> {
  const headers: HeadersInit = {};
  if (getToken) {
    const token = await getToken();
    if (token) headers['Authorization'] = `Bearer ${token}`;
  }
  return headers;
}

export async function apiGet<T>(path: string, query?: Record<string, string>): Promise<T> {
  const res = await fetch(buildUrl(path, query), {
    headers: await authHeaders(),
  });

  if (!res.ok) {
    const body = await res.json().catch(() => ({ error: res.statusText }));
    throw new Error(body.error ?? `HTTP ${res.status}`);
  }

  return res.json();
}

export async function apiPost<T>(path: string, body?: unknown): Promise<T> {
  const headers: HeadersInit = { ...await authHeaders(), 'Content-Type': 'application/json' };

  const res = await fetch(buildUrl(path), { method: 'POST', headers, body: JSON.stringify(body) });

  if (!res.ok) {
    const data = await res.json().catch(() => ({ error: res.statusText }));
    throw new Error(data.error ?? `HTTP ${res.status}`);
  }

  return res.json();
}

export async function apiPatch<T>(path: string, body?: unknown): Promise<T> {
  const headers: HeadersInit = { ...await authHeaders(), 'Content-Type': 'application/json' };

  const res = await fetch(buildUrl(path), { method: 'PATCH', headers, body: JSON.stringify(body) });

  if (!res.ok) {
    const data = await res.json().catch(() => ({ error: res.statusText }));
    throw new Error(data.error ?? `HTTP ${res.status}`);
  }

  return res.json();
}

export async function apiUpload<T>(path: string, formData: FormData): Promise<T> {
  const headers = await authHeaders();

  const res = await fetch(buildUrl(path), { method: 'POST', headers, body: formData });

  if (!res.ok) {
    const data = await res.json().catch(() => ({ error: res.statusText }));
    throw new Error(data.error ?? `HTTP ${res.status}`);
  }

  return res.json();
}

/**
 * Subscribe to a Server-Sent Events stream using fetch (supports auth headers).
 * EventSource doesn't support custom headers, so we use fetch + ReadableStream
 * to parse SSE events while still sending the Authorization header.
 * Returns an unsubscribe function.
 */
export function apiSSE(
  path: string,
  handlers: {
    onProgress?: (data: unknown) => void;
    onComplete?: (data: unknown) => void;
    onError?: (data: unknown) => void;
  }
): () => void {
  const controller = new AbortController();

  (async () => {
    try {
      const headers = await authHeaders();
      const res = await fetch(buildUrl(path), {
        headers: { ...headers, Accept: 'text/event-stream' },
        signal: controller.signal,
      });

      if (!res.ok || !res.body) {
        handlers.onError?.({ error: `HTTP ${res.status}` });
        return;
      }

      const reader = res.body.getReader();
      const decoder = new TextDecoder();
      let buffer = '';

      while (true) {
        const { done, value } = await reader.read();
        if (done) break;

        buffer += decoder.decode(value, { stream: true });
        const lines = buffer.split('\n');
        buffer = lines.pop() ?? '';

        let eventType = 'message';
        for (const line of lines) {
          if (line.startsWith('event:')) {
            eventType = line.slice(6).trim();
          } else if (line.startsWith('data:')) {
            const raw = line.slice(5).trim();
            if (!raw) continue;
            try {
              const data = JSON.parse(raw);
              if (eventType === 'progress') handlers.onProgress?.(data);
              else if (eventType === 'complete') { handlers.onComplete?.(data); controller.abort(); return; }
              else if (eventType === 'error') { handlers.onError?.(data); controller.abort(); return; }
            } catch { /* skip malformed JSON */ }
            eventType = 'message';
          }
        }
      }
    } catch (e) {
      if ((e as Error).name !== 'AbortError') {
        handlers.onError?.({ error: String(e) });
      }
    }
  })();

  return () => controller.abort();
}
